import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FileSpreadsheet, 
  Download, 
  Settings, 
  Calendar,
  Users,
  BarChart3,
  MapPin,
  CheckCircle,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import Button from '../common/Button';
import ScheduleExportModal from './ScheduleExportModal';
import { useAdminStore } from '../../store/adminStore';

interface ExportDataType {
  users: boolean;
  analytics: boolean;
  locations: boolean;
  categories: boolean;
}

interface ExportResult {
  success: boolean;
  spreadsheetUrl?: string;
  error?: string;
}

const ExportToSheets: React.FC = () => {
  const { users, analytics, exportToGoogleSheets } = useAdminStore();
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [exportUrl, setExportUrl] = useState('');
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [selectedDataTypes, setSelectedDataTypes] = useState<ExportDataType>({
    users: true,
    analytics: true,
    locations: true,
    categories: true
  });

  const handleExport = async () => {
    if (!Object.values(selectedDataTypes).some(Boolean)) {
      setExportStatus('error');
      setTimeout(() => setExportStatus('idle'), 3000);
      return;
    }

    setIsExporting(true);
    setExportStatus('idle');
    setExportUrl('');

    try {
      // Call the store method or direct API
      const result = await exportToGoogleSheets();
      
      if (result.success && result.spreadsheetUrl) {
        setExportStatus('success');
        setExportUrl(result.spreadsheetUrl);
      } else {
        throw new Error(result.error || 'Export failed');
      }
    } catch (error) {
      console.error('Export to Google Sheets failed:', error);
      setExportStatus('error');
    } finally {
      setIsExporting(false);
      setTimeout(() => setExportStatus('idle'), 10000);
    }
  };

  const exportOptions = [
    {
      id: 'users',
      title: 'User Data',
      description: 'Complete user profiles, contact information, and test completion status',
      icon: Users,
      count: users.length,
      enabled: selectedDataTypes.users
    },
    {
      id: 'analytics',
      title: 'Analytics Summary',
      description: 'Overall statistics, completion rates, and performance metrics',
      icon: BarChart3,
      count: Object.keys(analytics).length,
      enabled: selectedDataTypes.analytics
    },
    {
      id: 'locations',
      title: 'Geographic Data',
      description: 'User distribution by country and state with performance breakdown',
      icon: MapPin,
      count: analytics.locationData?.length || 0,
      enabled: selectedDataTypes.locations
    },
    {
      id: 'categories',
      title: 'Category Performance',
      description: 'Detailed breakdown of performance across all assessment categories',
      icon: FileSpreadsheet,
      count: analytics.categoryPerformance?.length || 0,
      enabled: selectedDataTypes.categories
    }
  ];

  const toggleDataType = (id: keyof ExportDataType) => {
    setSelectedDataTypes(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <motion.div
      className="bg-white rounded-xl shadow-card p-6 mt-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.9 }}
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary-600" />
            Export to Google Sheets
          </h3>
          <p className="text-gray-600 mt-1">
            Export comprehensive data for research and analysis purposes
          </p>
        </div>
        
        {exportStatus !== 'idle' && (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
            exportStatus === 'success' 
              ? 'bg-success-100 text-success-700' 
              : 'bg-error-100 text-error-700'
          }`}>
            {exportStatus === 'success' ? (
              <>
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Export Successful!</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Export Failed</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Success Message with Link */}
      {exportStatus === 'success' && exportUrl && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg"
        >
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-success-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-success-800 mb-1">Google Sheet Created Successfully!</h4>
              <p className="text-success-700 text-sm mb-3">
                Your data has been exported to Google Sheets with separate tabs for each data type.
              </p>
              <a
                href={exportUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors text-sm font-medium"
              >
                <ExternalLink className="w-4 h-4" />
                Open Google Sheet
              </a>
            </div>
          </div>
        </motion.div>
      )}

      {/* Export Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {exportOptions.map((option) => {
          const IconComponent = option.icon;
          return (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                option.enabled 
                  ? 'border-primary-200 bg-primary-50' 
                  : 'border-gray-200 bg-gray-50'
              }`}
              onClick={() => toggleDataType(option.id as keyof ExportDataType)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    option.enabled ? 'bg-primary-100' : 'bg-gray-100'
                  }`}>
                    <IconComponent className={`w-4 h-4 ${
                      option.enabled ? 'text-primary-600' : 'text-gray-500'
                    }`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">{option.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                  </div>
                </div>
                
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                  option.enabled 
                    ? 'border-primary-500 bg-primary-500' 
                    : 'border-gray-300'
                }`}>
                  {option.enabled && (
                    <CheckCircle className="w-3 h-3 text-white" />
                  )}
                </div>
              </div>
              
              <div className="text-sm text-gray-500">
                {option.count} records available
              </div>
            </div>
          );
        })}
      </div>

      {/* Export Summary */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h4 className="font-semibold text-gray-800 mb-3">Export Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-gray-600">Total Users</div>
            <div className="font-bold text-primary-600">{users.length}</div>
          </div>
          <div>
            <div className="text-gray-600">Completed Tests</div>
            <div className="font-bold text-success-600">{analytics.completedTests}</div>
          </div>
          <div>
            <div className="text-gray-600">Countries</div>
            <div className="font-bold text-secondary-600">
              {new Set(users.map(u => u.country)).size}
            </div>
          </div>
          <div>
            <div className="text-gray-600">Export Date</div>
            <div className="font-bold text-gray-800">
              {new Date().toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>

      {/* Export Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="primary"
          leftIcon={<Download />}
          onClick={handleExport}
          disabled={isExporting || !Object.values(selectedDataTypes).some(Boolean)}
          className="flex-1"
        >
          {isExporting ? 'Creating Google Sheet...' : 'Export to Google Sheets'}
        </Button>
        
        <Button
          variant="outline"
          leftIcon={<Calendar />}
          onClick={() => setIsScheduleModalOpen(true)}
        >
          Schedule Export
        </Button>
      </div>

      {/* Schedule Export Modal */}
      <ScheduleExportModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
      />
    </motion.div>
  );
};

export default ExportToSheets;